import { Box, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { formatISO, parseISO, subDays } from 'date-fns'

import Button from '@/components/button'
import { useCreateInfestationReport } from '@/hooks/mutations/use-create-infestation-report'

import { CultureSelectorInput } from './components/culture-selector-input'
import { PeriodOptionsInput } from './components/period-options-input'
import SortOptionsInput from './components/sort-options-input'
import { WeedClassSelectorInput } from './components/weed-class-selector-input'
import { DefaultNewReportForm } from './new-report-form-schema'
import { useStyles } from './styles'
import {
  caneOptions,
  NewReportFormProvider,
  soybeanOptions,
  useNewReportForm,
} from './use-new-report-form'

export interface NewReportFormProps {
  goBack: () => void
}

function NewReportFormComponent({ goBack }: NewReportFormProps) {
  const classes = useStyles()
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useNewReportForm()

  const { mutate, isLoading } = useCreateInfestationReport()

  const onSubmit = (data: DefaultNewReportForm) => {
    const isCane = data.culture === 'cane' || data.culture === 'all'
    const isSoybean = data.culture === 'soybean' || data.culture === 'all'
    const cane =
      isCane &&
      Object.keys(data.cane_options).filter(
        (option) => data.cane_options[option as keyof typeof caneOptions],
      )
    const soybean =
      isSoybean &&
      Object.keys(data.soybean_options).filter(
        (option) => data.soybean_options[option as keyof typeof soybeanOptions],
      )

    const today = new Date()
    const initial_conclusion_date =
      data.period.options === 'personalized'
        ? parseISO(data.period.personalized.start_date).setHours(0, 0, 0)
        : subDays(today, Number(data.period.options)).setHours(0, 0, 0)
    const final_conclusion_date =
      data.period.options === 'personalized'
        ? parseISO(data.period.personalized.end_date).setHours(23, 59, 59)
        : today

    const format = (date: number | Date) =>
      formatISO(date, { representation: 'complete' })

    mutate(
      {
        weed_type_by_culture: {
          cane: cane || [],
          soybean: soybean || [],
        },
        ordered_by: data.sort_options,
        initial_conclusion_date: format(initial_conclusion_date),
        final_conclusion_date: format(final_conclusion_date),
      },
      { onSuccess: goBack },
    )
  }

  return (
    <>
      <header className={classes.header}>
        <IconButton aria-label='Voltar' color='secondary' onClick={goBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant='h1'>Novo relatório de infestação</Typography>
      </header>

      <Box
        width='635px'
        margin='0 auto'
        height='100%'
        overflow='auto'
        padding='1rem'
        component={'form'}
        onSubmit={handleSubmit(onSubmit)}
      >
        <CultureSelectorInput />

        <WeedClassSelectorInput type='cane' />
        <WeedClassSelectorInput type='soybean' />

        <SortOptionsInput />

        <PeriodOptionsInput />

        <Box className={classes.buttonWrapper}>
          <Button outlined onClick={goBack}>
            Cancelar
          </Button>
          <Button loading={isLoading || isSubmitting} type='submit'>
            Gerar relatório de infestação
          </Button>
        </Box>
      </Box>
    </>
  )
}

export function NewReportForm(props: NewReportFormProps) {
  return (
    <NewReportFormProvider>
      <NewReportFormComponent {...props} />
    </NewReportFormProvider>
  )
}
